import { makeStyles, tokens, Toaster } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import { store } from '@store';
import i18n from '@locales';
import { ThemeProvider, FOOTER_HEIGHT } from '@theme';
import { mq } from '@/theme/scalingTokens';
import { railWidthExpanded, railWidthCollapsed } from '@/theme/customTheme';
import { AuthProvider } from '@auth/AuthProvider';
import { AuthGuard } from '@/routes/guards';
import { useAuth } from '@auth/useAuth';
import { Header } from '@layouts/Header';
import { Footer } from '@layouts/Footer';
import { LeftNavRail } from '@layouts/LeftNavRail';
import { MobileNavDrawer } from '@layouts/MobileNavDrawer';
import { AppRoutes } from '@/routes/AppRoutes';
import { ErrorBoundary } from '@components/common/ErrorBoundary';
import { useRailState } from '@/hooks/useRailState';
import { useIsMobile } from '@/hooks/useIsMobile';
import { APP_TOASTER_ID } from '@/hooks/useGlobalToast';
import { addNotification, type ToastIntent } from '@store/slices/notificationsSlice';

declare global {
  interface Window {
    __test_dispatch_toast?: (opts: { message: string; intent: ToastIntent }) => void;
  }
}

const useAppLayoutStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    paddingTop: '3rem',
    paddingBottom: FOOTER_HEIGHT,
    paddingLeft: 'var(--app-rail-width, 0px)',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground2,
    overflowX: 'hidden',
    overflowY: 'hidden',
    [mq.highZoom]: {
      height: 'auto',
      minHeight: '100vh',
      paddingBottom: 0,
      overflowY: 'visible',
    },
  },
});

function AppContent(): React.JSX.Element {
  const layoutStyles = useAppLayoutStyles();
  const { user } = useAuth();
  const { state: railState, toggle: toggleRail } = useRailState();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();
  // spec 101: the LeftNavRail and its WaffleToggle in the header are visible
  // only for privileged personas (Admin or Planner). Default Employee users
  // only have a single nav destination (Event Booking), so the rail is
  // irrelevant for them — hide rail + waffle entirely.
  const isPrivileged = user?.persona === 'Admin' || user?.persona === 'Planner';

  useEffect(() => {
    if (!import.meta.env.DEV) return undefined;
    window.__test_dispatch_toast = (opts) => {
      store.dispatch(addNotification(opts));
    };
    return () => {
      delete window.__test_dispatch_toast;
    };
  }, []);

  useEffect(() => {
    if (!isMobile && isMobileNavOpen) {
      setIsMobileNavOpen(false);
    }
  }, [isMobile, isMobileNavOpen]);

  // A11y fix: Fluent UI Tabster injects dummy <i> elements with both
  // aria-hidden="true" and tabindex="0", violating WCAG 4.1.2
  // (aria-hidden-focus). For dummies NOT inside a role="grid", strip
  // aria-hidden so role="none" handles screen-reader hiding.
  // For dummies inside a role="tablist", also remove tabindex so
  // role="none" is properly honored (focusable elements nullify role="none").
  // Dummies inside grids are handled separately by EventDataGrid.
  useEffect(() => {
    const isInsideGrid = (el: HTMLElement) => !!el.closest('[role="grid"]');
    const isInsideTablist = (el: HTMLElement) => !!el.closest('[role="tablist"]');
    const patchDummy = (el: HTMLElement) => {
      if (isInsideGrid(el)) return; // handled by EventDataGrid
      if (el.hasAttribute('aria-hidden')) el.removeAttribute('aria-hidden');
      if (isInsideTablist(el) && el.hasAttribute('tabindex')) el.removeAttribute('tabindex');
    };
    const fixTabsterDummies = (root: Node) => {
      if (!(root instanceof HTMLElement)) return;
      root.querySelectorAll<HTMLElement>('[data-tabster-dummy][aria-hidden]').forEach(patchDummy);
    };
    fixTabsterDummies(document.body);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            if (node.hasAttribute('data-tabster-dummy')) patchDummy(node);
            fixTabsterDummies(node);
          }
        }
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'aria-hidden' || mutation.attributeName === 'tabindex') &&
          mutation.target instanceof HTMLElement &&
          mutation.target.hasAttribute('data-tabster-dummy')
        ) {
          patchDummy(mutation.target);
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'tabindex'],
    });
    return () => observer.disconnect();
  }, []);

  // Expose the current left-nav-rail width as a CSS variable on the document
  // root so position:fixed consumers (e.g., FloatingActionBar wrappers around
  // the BookingsScreen "book another" card) can anchor to the section after
  // the rail without re-reading rail state themselves. The rail is hidden on
  // mobile (LeftNavRail returns null when isMobile), so the var must collapse
  // to 0 there — otherwise downstream consumers would inherit phantom rail
  // padding and render off-center.
  useEffect(() => {
    const railVisible = isPrivileged && !isMobile;
    const railWidth = railVisible
      ? railState === 'expanded'
        ? railWidthExpanded
        : railWidthCollapsed
      : '0px';
    document.documentElement.style.setProperty('--app-rail-width', railWidth);
    return () => {
      document.documentElement.style.removeProperty('--app-rail-width');
    };
  }, [isPrivileged, isMobile, railState]);

  return (
    <>
      <a
        href="#app-main"
        style={{
          position: 'absolute',
          top: '-100%',
          left: 0,
          width: '100%',
          zIndex: 10000,
          padding: '0.5rem 0',
          backgroundColor: tokens.colorNeutralBackgroundInverted,
          color: tokens.colorNeutralForegroundInverted,
          fontWeight: 400,
          fontSize: '0.875rem',
          textAlign: 'center',
          textDecoration: 'underline',
          transition: 'top 0.15s ease-in-out',
          boxSizing: 'border-box',
        }}
        onFocus={(e) => {
          e.currentTarget.style.top = '0';
        }}
        onBlur={(e) => {
          e.currentTarget.style.top = '-100%';
        }}
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById('app-main');
          if (!main) return;
          const target =
            main.querySelector<HTMLElement>('[data-skip-target]') ??
            main.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6') ??
            main;
          target.tabIndex = -1;
          target.focus();
          target.removeAttribute('tabindex');
        }}
      >
        Skip to main content
      </a>
      <Header
        appTitle={import.meta.env.VITE_APP_TITLE}
        displayName={user?.displayName}
        userEmail={user?.email}
        railState={isPrivileged ? railState : undefined}
        onToggleRail={isPrivileged ? toggleRail : undefined}
        onOpenMobileNav={isPrivileged ? () => setIsMobileNavOpen(true) : undefined}
      />
      <div className={layoutStyles.wrapper}>
        {isPrivileged && <LeftNavRail railState={railState} />}
        {isPrivileged && isMobile && (
          <MobileNavDrawer isOpen={isMobileNavOpen} onDismiss={() => setIsMobileNavOpen(false)} />
        )}
        <main
          id="app-main"
          style={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: tokens.colorNeutralBackground2,
            overflow: 'auto',
          }}
        >
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </main>
      </div>
      <Footer />
      <Toaster toasterId={APP_TOASTER_ID} position="top-end" />
    </>
  );
}

function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ReduxProvider store={store}>
          <I18nextProvider i18n={i18n}>
            <ThemeProvider>
              <AuthGuard>
                <AppContent />
              </AuthGuard>
            </ThemeProvider>
          </I18nextProvider>
        </ReduxProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
